import { Circ, Expo, TimelineLite, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { MathUtils } from "html-living-framework";
import { random } from "html-living-framework/lib/Utils/ArrayUtils";
import { isMobile } from "html-living-framework/lib/Utils/PlatformUtils";
import * as PIXI from "pixi.js";
import { Application } from "../Application";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import { CaptionToken } from "../data/CaptionToken";
import LightsOutGameModel from "../data/LightsOutGameModel";
import * as PlanterGameModel from "../data/PlanterGameModel";
import TrashGameModel from "../data/TrashGameModel";
import * as VO from "../data/VO";
import { randomExhaust } from "../data/VO";
import { Button, OutlinedButton } from "../gameobjects/Button";
import { Elmo } from "../gameobjects/Elmo";
import { LightsOutObject } from "../gameobjects/LightsOutObject";
import {LightsOutElmoBathroomLayout} from "../layout/LightsOutElmoBathroomLayout";
import { CaptionManager } from "../managers/CaptionManager";
import { LightsOutBadgePopup } from "../popups/LightsOutBadgePopup";
import {AbstractGameState} from "./AbstractGameState";
import { LightsOutElmoBathroomScreen } from "./LightsOutElmoBathroomScreen";
import { LightsOutElmoBedroomScreen } from "./LightsOutElmoBedroomScreen";
import { MainMenu } from "./MainMenu";

const lightCorrectAnswerVOs = randomExhaust([
    VO.LO06,
    VO.LO07,
]);
const waterCorrectAnswerVOs = randomExhaust([
    VO.LO08,
    VO.LO09,
]);
const genericCorrectAnswerVOs = randomExhaust([
    VO.LO04,
    VO.LO05,
]);

const elmoClickReactVO = [
    VO.LO12,
    VO.LO13,
];

HLF.ArrayUtils.shuffle(elmoClickReactVO);

const clickVO = isMobile() ? VO.LO_PROMPT01 : VO.LO_PROMPT02;

export abstract class AbstractLightsOutGameState extends AbstractGameState {

    public static newRoomVO = randomExhaust([VO.LO01, VO.LO02, VO.LO03]);
    public static allRoomsCompleteVO = randomExhaust([VO.LO14, VO.LO15]);

    public readonly elmo = this.addChild(new Elmo());
    public readonly homeButton = this.addChild(new OutlinedButton());

    protected _subscriptions: any[] = [];

    protected abstract itemsToTurnOff: LightsOutObject[];
    protected abstract readonly roomID: "Bedroom" | "Bathroom" | "Kitchen" | "LivingRoom";

    protected abstract roomNameVO: string;
    protected introVo: string[] = [];
    protected clickCount: number = 0;
    protected analyticsRoundName = "Lights Out";
    protected screenStartTime: number = 0;
    protected elmoStartPosition?: PIXI.IPoint;

    public init(pSize: PIXI.Point, pData?: any) {
        this._subscriptions.push(PubSub.subscribe("item clicked", this.onItemClicked.bind(this)));
        this._subscriptions.push(PubSub.subscribe("item turned off", this.onItemTurnedOff.bind(this)));
        this._subscriptions.push(PubSub.subscribe("item turned on", this.onItemTurnedOn.bind(this)));
        LightsOutGameModel.rooms[this.roomID].visited = true;
        LightsOutGameModel.rooms[this.roomID].completed = false;
        this.homeButton.onClick.add(() => {
            this.trackEvent("gameMenuClick", {buttonName: "Home"});
            this.gotoNextState("MainMenu");
        });
        this.homeButton.clickSfx = "buttonclick";

        if (pData?.fromIntro) {
            this.introVo = [VO.LO01, this.roomNameVO];
        }
        else {
            this.introVo = [AbstractLightsOutGameState.newRoomVO(), this.roomNameVO];
        }

        if (pData?.fromMainMenu) {
            this.trackEvent("gameRoundStart", {roundNumber: Application.instance.round});
            LightsOutGameModel.roundStartTime = new Date().valueOf();
        }

        this.trackEvent("gameComponentStart", {
            componentType: "Simulation",
            componentName: "Lights Out",
        });
        this.screenStartTime = new Date().valueOf();

        this.elmo.interactive = true;
        this.elmo.buttonMode = true;
        this.elmo.accessible = true;
        this.elmo.addListener(HLF.Input.Events.POINTER_TAP, () => {
            if (this.idleStartTime !== undefined) {
                const idleEndTime = new Date().valueOf();
                const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                this.trackEvent("gameTOReEngage", {duration: idleDuration});
                this.idleStartTime = undefined;
            }
            this.idleTimeline?.restart(true);
            this.itemsToTurnOff.forEach((it) => it.stopPulse());

            const voToPlay = elmoClickReactVO[0];

            // cycle vo only if it actually played
            const onVoDidPlay = (_pTopic: string, _pData: string) => {
                if (_pData === voToPlay) {
                    if (elmoClickReactVO[0] === voToPlay) {
                        elmoClickReactVO.push(elmoClickReactVO.shift()!);
                    }
                }
                PubSub.unsubscribe(token);
            };

            const token = PubSub.subscribe(HLF.VOICEOVER_STARTED, onVoDidPlay);

            Application.instance.voiceover.playVO(voToPlay!, HLF.PlayMode.New, () => {
                PubSub.unsubscribe(token);
                this.idleTimeline?.restart(true);
            });
        });
        super.init(pSize, pData);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);

        this.elmoStartPosition = this.elmo.position.clone();

        const bgm = HLF.Application.instance.audio.getAudioTrack("lights_out_loop", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("lights_out_loop", HLF.AudioCategory.BGM);
        if (bgm.isPlaying() === false) {
            bgm.setLooped(true);
            bgm.play();
        }
        this.itemsToTurnOff.forEach((it) => {
            if (!it.turnedOff && it.objectType !== "easteregg") {
                it.pulse();
            }
        });
        Application.instance.voiceover.playVO(this.introVo, {
            skipCC: true,
            callback: () => {
                this.itemsToTurnOff.forEach((it) => it.stopPulse());
                this.idleTimeline.pause();
                this.idleTimeline.call(() => {
                    this.trackEvent("gameTimeoutPlay");
                    if (this.idleStartTime === undefined) {
                        this.idleStartTime = new Date().valueOf();
                    }

                    const itemsTurnedOffCount = this.itemsToTurnOff.filter((it) => it.turnedOff).length;
                    this.itemsToTurnOff.forEach((it) => {
                        if (!it.turnedOff && it.objectType !== "easteregg") {
                            it.pulse();
                        }
                    });
                    if (this.clickCount === 0) {
                        Application.instance.voiceover.playVO([clickVO, VO.LO_TO01], {
                            mode: HLF.PlayMode.New,
                            callback: () => {
                                this.idleTimeline?.restart(true);
                                this.itemsToTurnOff.forEach((it) => it.stopPulse());
                            },
                            skipCC: true,
                            priority: -1,
                        });

                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(VO.LO_TO01, 0, {
                            prompt: clickVO,
                        }));
                    }
                    else if (this.itemsToTurnOff.length === itemsTurnedOffCount + 1) {

                        const lastItem = this.itemsToTurnOff.filter((it) => !it.turnedOff)[0];
                        if (lastItem.objectType === "refrigerator") {
                            Application.instance.voiceover.playVO(VO.LO_TO04, {
                                mode: HLF.PlayMode.New,
                                callback: () => {
                                    this.idleTimeline?.restart(true);
                                    this.itemsToTurnOff.forEach((it) => it.stopPulse());
                                },
                                priority: -1,
                            });
                        }
                        else {
                            Application.instance.voiceover.playVO(VO.LO_TO03, {
                                mode: HLF.PlayMode.New,
                                callback: () => {
                                    this.idleTimeline?.restart(true);
                                    this.itemsToTurnOff.forEach((it) => it.stopPulse());
                                },
                                priority: -1,
                            });
                        }
                    }
                    else if (this.clickCount > 0) {
                        Application.instance.voiceover.playVO([VO.LO_TO02, this.roomNameVO], {
                            mode: HLF.PlayMode.New,
                            callback: () => {
                                this.idleTimeline?.restart(true);
                                this.itemsToTurnOff.forEach((it) => it.stopPulse());
                            },
                            skipCC: true,
                            priority: -1,
                        });
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(VO.LO_TO02, 0, {
                            room: this.roomNameVO,
                        }));
                    }
                });
                this.idleTimeline.restart(true);
            },
        });

        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(this.introVo[0], 0, {
            room: this.roomNameVO,
        }));
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.elmo.update(pDeltaTime);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        HLF.PixiUtils.sendToFront(this.homeButton);
        HLF.PixiUtils.sendToFront(this.elmo);
        this.homeButton.position.x = ((1664 - pSize.x) / 2) + 108;
    }

    public destroy() {
        super.destroy();
        for (const subscription of this._subscriptions) {
            PubSub.unsubscribe(subscription);
        }
        this.idleTimeline?.kill();
        this.idleTimeline?.clear();
    }

    protected onItemClicked(pTopic: "item clicked", pData: LightsOutObject) {
        this.idleTimeline?.restart(true);
        this.itemsToTurnOff.forEach((it) => it.stopPulse());
        if (this.idleStartTime !== undefined) {
            const idleEndTime = new Date().valueOf();
            const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
            this.trackEvent("gameTOReEngage", {duration: idleDuration});
            this.idleStartTime = undefined;
        }
        const itemsTurnedOffCount = this.itemsToTurnOff.filter((it) => it.turnedOff).length;

        this.trackEvent("gameInteraction", {objectName: pData.name.replace(/^\w/, (c) => c.toUpperCase())});

        const remainingItems = this.itemsToTurnOff.filter((it) => !it.turnedOff);
        if (remainingItems.includes(pData)) {
            this.clickCount++;
            this.trackEvent("gameQuestionCA", {
                questionAsked: remainingItems.map((it) => it.name.replace(/^\w/, (c) => c.toUpperCase())).join(", "),
                questionNumber: this.clickCount + 1,
                playerAnswer: pData.name.replace(/^\w/, (c) => c.toUpperCase()),
            });
        }
        else if (this.itemsToTurnOff.includes(pData)) {
            this.trackEvent("gameQuestionWA", {
                questionAsked: remainingItems.map((it) => it.name.replace(/^\w/, (c) => c.toUpperCase())).join(", "),
                questionNumber: this.clickCount + 1,
                playerAnswer: pData.name.replace(/^\w/, (c) => c.toUpperCase()),
            });
        }
    }

    protected onItemTurnedOn(pTopic: "item turned on", pData: LightsOutObject) {
        //
    }

    protected onItemTurnedOff(pTopic: "item turned off", pData: LightsOutObject) {
        if (LightsOutGameModel.rooms[this.roomID].completed) {
            return;
        }

        this.idleTimeline?.restart(true);
        this.itemsToTurnOff.forEach((it) => it.stopPulse());

        const itemsTurnedOffCount = this.itemsToTurnOff.filter((it) => it.turnedOff).length;

        let correctAnswerVO: string | undefined;
        if (this.clickCount % 3 === 0) {
            switch (pData.objectType) {
                case "light":
                    correctAnswerVO = lightCorrectAnswerVOs();
                    break;
                case "water":
                    correctAnswerVO = waterCorrectAnswerVOs();
                    break;
                case "electricity":
                case "refrigerator":
                    correctAnswerVO = genericCorrectAnswerVOs();
                    break;
                // intentionally omitted case "easteregg" / case default
            }

        }
        if (correctAnswerVO) {
            Application.instance.voiceover.playVO(correctAnswerVO, {
                mode: HLF.PlayMode.Override,
                callback: () => {
                    this.idleTimeline?.restart(true);
                },
                priority: 3, // don't let this get interrupted
            });
        }
    }

    protected makeSwitch(pSwitch: LightsOutObject, pTarget: LightsOutObject) {
        pSwitch.onDown.add(() => {
            if (!pTarget.turnedOff) {
                const destX = MathUtils.clamp(
                    pSwitch.x + Math.abs(this.elmo.width / 2) * Math.sign(this.elmo.x - pSwitch.x),
                    (1664 - this._size.x) / 2 + Math.abs(this.elmo.width / 2),
                    (1664 - this._size.x) / 2 + this._size.x - Math.abs(this.elmo.width / 2),
                );
                this.elmo.walkTo(destX, 300, Ease.none, () => {
                    pSwitch.TurnOff();
                    this.elmo.Grab(pSwitch, () => {
                        HLF.Application.instance.audio.play("correctanswer", 1, false, HLF.AudioCategory.SFX);
                        const itemsTurnedOffCount = this.itemsToTurnOff.filter((it) => it.turnedOff).length;
                        if (this.itemsToTurnOff.length === itemsTurnedOffCount) {
                            this.onAllItemsTurnedOff();
                        }
                        this.elmo.walkTo(this.elmoStartPosition!.x, 100, Ease.none, () => {
                            //
                        });
                    });
                });
            }
        });
        pSwitch.onOver.add((_pSwitch, pEvent) => {
            if (!pTarget.turnedOff) {
                pTarget.Highlight();
            }
        });
        pSwitch.onOut.add((_pSwitch, pEvent) => {
            pTarget.UnHighlight();
        });

        pTarget.onDown.add(() => {
            if (!pTarget.turnedOff) {
                const destX = MathUtils.clamp(
                    pSwitch.x + Math.abs(this.elmo.width / 2) * Math.sign(this.elmo.x - pSwitch.x),
                    (1664 - this._size.x) / 2 + Math.abs(this.elmo.width / 2),
                    (1664 - this._size.x) / 2 + this._size.x - Math.abs(this.elmo.width / 2),
                );
                this.elmo.walkTo(destX, 200, Ease.none, () => {
                    pTarget.TurnOff();
                    pSwitch.TurnOff();
                    this.elmo.Grab(pSwitch, () => {
                        HLF.Application.instance.audio.play("correctanswer", 1, false, HLF.AudioCategory.SFX);
                        const itemsTurnedOffCount = this.itemsToTurnOff.filter((it) => it.turnedOff).length;
                        if (this.itemsToTurnOff.length === itemsTurnedOffCount) {
                            this.onAllItemsTurnedOff();
                        }
                        this.elmo.walkTo(this.elmoStartPosition!.x, 100, Ease.none, () => {
                            //
                        });
                    });
                });
            }
        });

        pSwitch.switched = pTarget;
    }

    protected makeObjectClickable(pObject: LightsOutObject) {
        if (pObject.objectType === "easteregg") {
            pObject.onDown.add(() => {
                Application.instance.voiceover.playVO(VO.LO12, HLF.PlayMode.New);
                if (pObject.toggle && pObject.selected) {
                    pObject.TurnOn();
                }
                else if (pObject.toggle) {
                    pObject.TurnOff();
                }
                else {
                    pObject.TurnOn();
                }
            });
            if (pObject.toggle) {
                pObject.selected = true;
            }
            pObject.TurnOff({animate: false});
        }
        else {
            pObject.onDown.add(() => {
                if (!pObject.turnedOff) {
                    const destX = MathUtils.clamp(
                        pObject.x + Math.abs(this.elmo.width / 2) * Math.sign(this.elmo.x - pObject.x),
                        (1664 - this._size.x) / 2 + Math.abs(this.elmo.width / 2),
                        (1664 - this._size.x) / 2 + this._size.x - Math.abs(this.elmo.width / 2),
                    );
                    this.elmo.walkTo(destX, 200, Ease.none, () => {
                        pObject.TurnOff();
                        this.elmo.Grab(pObject, () => {
                            HLF.Application.instance.audio.play("correctanswer", 1, false, HLF.AudioCategory.SFX);
                            const itemsTurnedOffCount = this.itemsToTurnOff.filter((it) => it.turnedOff).length;
                            if (this.itemsToTurnOff.length === itemsTurnedOffCount) {
                                this.onAllItemsTurnedOff();
                            }
                            this.elmo.walkTo(this.elmoStartPosition!.x, 100, Ease.none, () => {
                                //
                            });
                        });
                    });
                }
            });
        }
    }

    protected onAllItemsTurnedOff() {
        if (LightsOutGameModel.rooms[this.roomID].completed) {
            return;
        }
        this.idleTimeline?.kill();
        this.idleTimeline?.clear();
        this.homeButton.visible = false;
        TweenLite.delayedCall(1, () => {
            this.elmo.Wave();
            Application.instance.audio.play("lights-out-roomfinish", 1, false, HLF.AudioCategory.SFX);
        });
        LightsOutGameModel.rooms[this.roomID].completed = true;
        this.trackEvent("gameComponentComplete", {
            componentType: "Simulation",
            componentName: "Lights Out",
            duration: (new Date().valueOf() - this.screenStartTime) / 1000,
        });
        // TODO: don't use strings here
        const nextRoom: string[] = [];

        if (!LightsOutGameModel.rooms.Bedroom.completed) {
            nextRoom.push("LightsOutElmoBedroomScreen");
        }
        if (!LightsOutGameModel.rooms.Bathroom.completed) {
            nextRoom.push("LightsOutElmoBathroomScreen");
        }
        if (!LightsOutGameModel.rooms.Kitchen.completed) {
            nextRoom.push("LightsOutElmoKitchenScreen");
        }
        if (!LightsOutGameModel.rooms.LivingRoom.completed) {
            nextRoom.push("LightsOutElmoLivingRoomScreen");
        }

        TweenLite.delayedCall(3, () => {
            if (nextRoom.length > 1) {
                this.gotoNextState(HLF.ArrayUtils.random(nextRoom));
            }
            else {
                this.trackEvent("gameRoundComplete", {
                    roundNumber: Application.instance.round,
                    duration: (new Date().valueOf() - LightsOutGameModel.roundStartTime) / 1000,
                });
                if (LightsOutGameModel.round === Application.instance.playthrough && PlanterGameModel.GameState.round > Application.instance.playthrough && TrashGameModel.round > Application.instance.playthrough) {
                    this.trackEvent("gamePlaythroughComplete", {
                        duration: (new Date().valueOf() - Application.instance.playthroughStartTime) / 1000,
                        playthroughNumber: Application.instance.playthrough,
                    });
                    Application.instance.playthrough++;
                    Application.instance.playthroughStartTime = new Date().valueOf();
                }
                Application.instance.round++;
                LightsOutGameModel.round++;
                Application.instance.voiceover.playVO(AbstractLightsOutGameState.allRoomsCompleteVO(), HLF.PlayMode.Append, () => {
                    this.elmo.visible = false;
                    LightsOutGameModel.reset();
                    this.trackEvent("gameAchievement", {achievementType: "Lights Out Badge", roundNumber: (Application.instance.round - 1)});
                    this._subscriptions.push(PubSub.subscribe("home button clicked", () => this.trackEvent("gameMenuClick", {buttonName: "Home"})));
                    this._subscriptions.push(PubSub.subscribe("home button auto clicked", () => this.trackEvent("gameMenuClick", {buttonName: "AutoHome"})));
                    this._subscriptions.push(PubSub.subscribe("blackout clicked", () => this.trackEvent("gameMenuClick", {buttonName: "NotHome"})));
                    PubSub.publishSync(HLF.SHOW_POPUP, new HLF.PopupToken(LightsOutBadgePopup.NAME, this.onBadgePopupClosed.bind(this), true, false));
                });
            }
        });
    }
    protected onBadgePopupClosed() {
        this.elmo.visible = true;
    }
}
